import { useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  useToast,
  List,
  ListItem,
  Flex,
} from '@chakra-ui/react';
import { useTimerStore } from '../store/timerStore';
import { useAuthStore } from '../store/authStore';
import { useSessionStore } from '../store/sessionStore';
import { useSettingsStore } from '../store/settingsStore';

export const Timer = () => {
  const toast = useToast();
  const { isOpen: isResetOpen, onOpen: onResetOpen, onClose: onResetClose } = useDisclosure();
  const { isOpen: isSessionOpen, onOpen: onSessionOpen, onClose: onSessionClose } = useDisclosure();
  const { duration, timeLeft, isActive, isPaused, startTimer, pauseTimer, continueTimer, resetTimer, updateTimeLeft, setDuration, isOvertime, overtimeSeconds } = useTimerStore();
  const { user, signInWithGoogle } = useAuthStore();
  const { sessions, addSession, loadSessions, loadMoreSessions, hasMore, isLoading } = useSessionStore();
  const { playNotificationSound } = useSettingsStore();

  // Preload audio
  const notificationAudio = new Audio('/mixkit-happy-bells-notification-937.wav');

  const requestNotificationPermission = async () => {
    try {
      if (!("Notification" in window)) {
        console.log("This browser does not support notifications");
        toast({
          title: "Notifications not supported",
          description: "Your browser doesn't support notifications.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return false;
      }

      console.log("Current notification permission:", Notification.permission);

      if (Notification.permission === "denied") {
        toast({
          title: "Notifications blocked",
          description: "Please enable notifications in your browser settings to receive timer alerts.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return false;
      }

      if (Notification.permission !== "granted") {
        const permission = await Notification.requestPermission();
        console.log("Permission request result:", permission);
        
        if (permission === "granted") {
          toast({
            title: "Notifications enabled",
            description: "You'll be notified when your timer completes.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          return true;
        }
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  };

  const handleStartTimer = async () => {
    console.log('Starting timer...');
    startTimer();
    
    // Request notification permission at the start
    if (Notification.permission !== "granted") {
      await requestNotificationPermission();
    }

    // Preload audio by playing it silently
    if (playNotificationSound) {
      notificationAudio.volume = 0;
      notificationAudio.play().catch(e => console.log("Error preloading sound:", e));
      notificationAudio.pause();
      notificationAudio.currentTime = 0;
      notificationAudio.volume = 1;
    }
  };

  const sendNotification = async () => {
    try {
      console.log("Attempting to send notification, permission:", Notification.permission);
      
      // Play sound first as it's more reliable
      if (playNotificationSound) {
        try {
          await notificationAudio.play();
        } catch (e) {
          console.log("Error playing sound:", e);
        }
      }

      // Check if we need to request permission again (some browsers require interaction)
      if (Notification.permission !== "granted") {
        const granted = await requestNotificationPermission();
        if (!granted) return;
      }

      // Create and show notification
      if (Notification.permission === "granted") {
        const notification = new Notification("Time's Up! 🎯", {
          body: "Your focus session has ended. What did you accomplish?",
          // requireInteraction: true,
          // icon: "/logo.png", // Add app icon to make notification more visible
          // silent: true, // We handle the sound separately
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
        };

        console.log("Notification sent successfully");
      } else {
        console.log("Cannot send notification - permission not granted");
        toast({
          title: "Timer Complete",
          description: "Your focus session has ended!",
          status: "info",
          duration: null,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      // Fallback to toast notification
      toast({
        title: "Timer Complete",
        description: "Your focus session has ended!",
        status: "info",
        duration: null,
        isClosable: true,
      });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(Math.abs(seconds) / 60);
    const secs = Math.abs(seconds) % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const newDuration = parseInt(e.target.value) * 60;
  //   if (!isNaN(newDuration) && newDuration > 0) {
  //     setDuration(newDuration);
  //   }
  // };

  const handleSessionComplete = async (description: string) => {
    if (user) {
      const totalDuration = duration + overtimeSeconds; // Include overtime in total duration
      console.log('Attempting to save session:', { userId: user.uid, description, duration: totalDuration });
      try {
        await addSession(user.uid, description, totalDuration);
        console.log('Session saved successfully');
        onSessionClose();
        resetTimer();
        toast({
          title: "Session completed!",
          description: "Your focus session has been saved.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error saving session:', error);
        toast({
          title: "Error",
          description: "Failed to save your session. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      console.error('No user found when trying to save session');
    }
  };

  useEffect(() => {
    console.log('Timer state:', { isActive, isPaused, timeLeft });
    let animationFrameId: number;

    const updateTimer = () => {
      updateTimeLeft();
      animationFrameId = requestAnimationFrame(updateTimer);
    };

    if (isActive && !isPaused) {
      console.log('Starting animation frame');
      animationFrameId = requestAnimationFrame(updateTimer);
    }

    return () => {
      if (animationFrameId) {
        console.log('Canceling animation frame');
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isActive, isPaused, updateTimeLeft]);

  useEffect(() => {
    if (timeLeft === 0 && !isPaused && isOvertime && user) {
      // Only send notification when first hitting zero
      sendNotification();
    }
  }, [timeLeft, isPaused, isOvertime, user]);

  useEffect(() => {
    if (user) {
      loadSessions(user.uid);
    }
  }, [user]);

  if (!user) {
    return (
      <VStack spacing={4} p={8} w="100%" align="center">
        <Text>Please sign in to use the Pomodoro Timer</Text>
        <Button colorScheme="blue" onClick={signInWithGoogle}>
          Sign in with Google
        </Button>
      </VStack>
    );
  }

  return (
    <Box w="100%" maxW="1200px" mx="auto">
      <VStack spacing={8} align="stretch" w="100%">
        <Flex 
          gap={8} 
          flexDir={{ base: 'column', md: 'row' }}
          alignItems="center"
          justifyContent="center"
        >
          <Box
            flex={{ base: '1', md: '0 0 400px' }}
            h={{ base: '300px', md: '400px' }}
            borderRadius="full"
            border="4px solid"
            borderColor={isOvertime ? "orange.500" : "blue.500"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            bg="white"
            shadow="lg"
          >
            <VStack spacing={2}>
              <Text fontSize={{ base: '6xl', md: '7xl' }} fontWeight="bold" color={isOvertime ? "orange.500" : "inherit"}>
                {formatTime(timeLeft)}
              </Text>
              {isOvertime && (
                <Text fontSize="lg" color="orange.500" fontWeight="semibold">
                  +{formatTime(overtimeSeconds)}
                </Text>
              )}
            </VStack>
          </Box>

          <VStack
            spacing={4}
            flex="1"
            justify="center"
            align={{ base: 'center', md: 'center' }}
            p={4}
            maxW={{ base: '100%', md: '300px' }}
          >
            {!isActive ? (
              <Button size="lg" w={{ base: 'full', md: 'full' }} colorScheme="blue" onClick={handleStartTimer}>
                Start Focus Session
              </Button>
            ) : isPaused ? (
              <Button size="lg" w={{ base: 'full', md: 'full' }} colorScheme="green" onClick={continueTimer}>
                Continue
              </Button>
            ) : (
              <>
                <Button size="lg" w={{ base: 'full', md: 'full' }} colorScheme="orange" onClick={pauseTimer}>
                  Pause
                </Button>
                {isOvertime && (
                  <Button 
                    size="lg" 
                    w={{ base: 'full', md: 'full' }} 
                    colorScheme="green"
                    onClick={onSessionOpen}
                  >
                    End Session
                  </Button>
                )}
              </>
            )}
            <Button size="lg" w={{ base: 'full', md: 'full' }} colorScheme="red" onClick={onResetOpen}>
              Reset
            </Button>
            {user.uid === "N5JoawpVkcMxvYdYHxrfWXFvygt1" && (
              <Button size="md" w={{ base: 'full', md: 'full' }} colorScheme="gray" onClick={() => setDuration(25 * 60)}>
                Set to 25 minutes
              </Button>
            )}
            {/* Only show this button for the user with uid "N5JoawpVkcMxvYdYHxrfWXFvygt1" - storesund.hetland@gmail.com */}
            {user.uid === "N5JoawpVkcMxvYdYHxrfWXFvygt1" && (
              <Button size="md" w={{ base: 'full', md: 'full' }} colorScheme="gray" onClick={() => setDuration(5)}>
                Set to 5 seconds
              </Button>
            )}
          </VStack>
        </Flex>

        {sessions.length > 0 && (
          <Box 
            w="100%" 
            p={6} 
            bg="white" 
            shadow="md" 
            borderRadius="lg"
            mt={8}
          >
            <Flex justify="space-between" align="baseline" mb={4}>
              <Text fontSize="2xl" fontWeight="bold">Recent Sessions</Text>
              <Text fontSize="sm" color="gray.600">
                Showing {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
              </Text>
            </Flex>
            {sessions.length >= 2 && (
              <Text fontSize="sm" color="gray.600" mb={4}>
                From{' '}
                {new Date(sessions[sessions.length - 1].completedAt.toDate()).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                {' '}to{' '}
                {new Date(sessions[0].completedAt.toDate()).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </Text>
            )}
            <List spacing={3} maxH="500px" overflowY="auto">
              {sessions.map((session) => (
                <ListItem 
                  key={session.id} 
                  p={4} 
                  bg="gray.50" 
                  borderRadius="md"
                  _hover={{ bg: 'gray.100' }}
                  transition="background 0.2s"
                >
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontWeight="bold">{session.description}</Text>
                    <Text fontSize="sm" color="blue.600" fontWeight="medium">
                      {session.duration < 60 ? `${session.duration} seconds` : `${Math.floor(session.duration / 60)} minutes`}
                    </Text>
                  </Flex>
                  <Text fontSize="sm" color="gray.600">
                    {new Date(session.completedAt.toDate()).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Text>
                </ListItem>
              ))}
              {hasMore && (
                <Button
                  isLoading={isLoading}
                  onClick={() => loadMoreSessions(user.uid)}
                  variant="ghost"
                  size="sm"
                  width="100%"
                  mt={2}
                >
                  Load More Sessions
                </Button>
              )}
            </List>
          </Box>
        )}
      </VStack>

      <Modal isOpen={isResetOpen} onClose={onResetClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reset Timer?</ModalHeader>
          <ModalBody>
            Are you sure you want to reset the timer? Your progress will be lost.
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={() => {
              resetTimer();
              onResetClose();
            }}>
              Reset
            </Button>
            <Button variant="ghost" onClick={onResetClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isSessionOpen} onClose={onSessionClose} closeOnOverlayClick={false} closeOnEsc={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Session Complete!</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel>What did you accomplish in this session?</FormLabel>
              <Input
                id="session-description"
                placeholder="Describe your work..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const input = e.target as HTMLInputElement;
                    if (input.value.trim()) {
                      handleSessionComplete(input.value);
                    }
                  }
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={() => {
              const input = document.getElementById('session-description') as HTMLInputElement;
              if (input && input.value.trim()) {
                handleSessionComplete(input.value);
              }
            }}>
              Save Session
            </Button>
            <Button variant="ghost" colorScheme="red" onClick={() => {
              if (window.confirm("Are you sure you want to close without saving your session? Your progress will not be recorded.")) {
                onSessionClose();
                resetTimer();
              }
            }}>
              Discard
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}; 